/**
 * Asking a platform for history, which this product has never been able to do.
 *
 * Sync fetches the last seven days. Not "by default" in any way a caller can
 * override — `triggerSync` has accepted `startDate`/`endDate` since it was
 * written and not one of its three call sites has ever passed them, and the
 * server-side enqueue has never written those columns at all. So a workspace
 * connecting an account receives seven days of history and nothing before it,
 * for as long as the account exists.
 *
 * What that costs is not theoretical. Measured on one account on 2026-09-15:
 * the database held ฿1,732,104 of Facebook spend against ฿4,197,326 the Graph
 * API reported for the same period — 41%. Five whole months were absent, and
 * every figure the product showed for that channel, on every page, was
 * understated by more than half. Recovering it took an operator with shell
 * access hand-writing job rows.
 *
 * This is that operation, as a feature.
 *
 * WHY MONTHLY CHUNKS rather than one job for the whole range. Three separate
 * reasons, each learned from a real failure:
 *
 *   - The worker reaps jobs it believes abandoned after three hours. A long
 *     backfill outlives that and is requeued while it is still running, so the
 *     job row lies and the API budget is spent twice.
 *   - Google's client has no pagination at all: a truncated answer is
 *     byte-for-byte identical to a complete one. Short windows keep responses
 *     under any cap rather than trusting there isn't one.
 *   - Google, TikTok and LINE preserve no partial progress — a failure in the
 *     last window discards every earlier one. A month lost is a month, not a
 *     year.
 *
 * Re-running a chunk is safe and is how a partial recovery finishes: metric
 * writes upsert on `(ad_set_id, date)` and `(ad_id, date)`, so a second pass
 * overwrites and never duplicates.
 *
 * @module services/backfillService
 */

import { getSupabaseClient } from '@/config';
import { logError } from '@/services/logger';
import { todayDataDate, shiftDataDate, daysBetween } from '@/services/dataDate';
import type { TFn } from '@/utils/channelCorrelation';
import { getPlatformAccount, toDbPlatform } from '@/services/platformAccountService';
import { ACTIVE_JOB_STATUSES, isAlreadyQueued } from '@/services/syncJobShared';

/**
 * How far back a newly connected account reaches.
 *
 * Twelve months because that is the floor a marketing-mix model is conventionally
 * fitted on, and /mmm's readiness check states the same number: a workspace that
 * connects today should be a year of history closer to a real model tomorrow,
 * rather than starting a year-long wait from zero.
 */
export const DEFAULT_BACKFILL_MONTHS = 12;

/** A single job's window. */
export interface BackfillChunk {
  start: string;
  end: string;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The last day of a month: the first of the next one, stepped back a day —
 * without a table of month lengths or a leap-year rule to get wrong.
 */
const lastDayOfMonth = (year: number, month: number): string =>
  shiftDataDate(month === 12 ? `${year + 1}-01-01` : `${year}-${pad2(month + 1)}-01`, -1);

/**
 * Split an inclusive date range into calendar-month windows, NEWEST FIRST.
 *
 * Newest first because that is the order the value arrives in. Recent months
 * carry the numbers somebody is looking at today; the oldest months of a
 * twelve-month reach are frequently before the account ran at all, and running
 * those last means a failure lands where it costs least.
 *
 * Partial months at either end stay partial — a range of the 10th to the 20th
 * is one chunk of exactly those days, not a whole month with the edges
 * silently widened into API calls nobody asked for.
 */
export const monthlyChunks = (start: string, end: string): BackfillChunk[] => {
  if (daysBetween(start, end) < 0) return [];

  const chunks: BackfillChunk[] = [];
  let cursor = start;
  while (daysBetween(cursor, end) >= 0) {
    const [y, m] = cursor.split('-').map(Number);
    const monthEnd = lastDayOfMonth(y, m);
    const chunkEnd = daysBetween(monthEnd, end) < 0 ? end : monthEnd;
    chunks.push({ start: cursor, end: chunkEnd });
    cursor = shiftDataDate(chunkEnd, 1);
  }
  return chunks.reverse();
};

/** The range a connect-time backfill covers, ending yesterday. */
export const defaultBackfillRange = (months = DEFAULT_BACKFILL_MONTHS): BackfillChunk => {
  // Ends yesterday, not today: the routine sync already covers the last seven
  // days, and today is still accruing on every platform.
  const end = shiftDataDate(todayDataDate(), -1);
  const [y, m, d] = end.split('-').map(Number);
  const startYear = y + Math.floor((m - 1 - months) / 12);
  const startMonth = ((((m - 1 - months) % 12) + 12) % 12) + 1;
  // The same day of the month — or that month's last day when it has no such
  // day. Six months before 31 October is not 31 April: that string chunks into
  // a first month that starts after it ends, Postgres refuses the row, and the
  // whole batch goes with it for every account, for as long as yesterday is a
  // day the start month lacks (first: 2026-11-01, the six-month option).
  const startDay = Math.min(d, Number(lastDayOfMonth(startYear, startMonth).slice(8)));
  return {
    start: `${startYear}-${pad2(startMonth)}-${pad2(startDay)}`,
    end,
  };
};

/** What a request did to the queue — for one account, or summed over several. */
export interface BackfillOutcome {
  /** Chunks THIS request put on the queue. Zero when the whole range was already waiting. */
  queued: number;
  /**
   * Chunks of the range an earlier request already has waiting, left as they
   * are. Kept apart from `queued` so a repeat click can say it sent nothing —
   * one number for both told the reader a second batch of platform requests
   * went out when none did.
   */
  alreadyQueued: number;
}

/** One click, summed over the accounts it covered. */
export interface BackfillBatchOutcome extends BackfillOutcome {
  /** Accounts whose request failed while another's went through, each with its own reason. */
  failures: Array<{ accountId: string; reason: string }>;
}

export interface BackfillRequest extends BackfillOutcome {
  first: string;
  last: string;
}

/**
 * Enqueue a backfill for one account.
 *
 * Writes `sync_jobs` rows and returns: the worker drains them, exactly as it
 * drains an ordinary sync. Deliberately NOT routed through
 * `scripts/trigger-sync.ts`, whose `syncAll()` path loads accounts with no
 * workspace filter and keys its client map by platform, so the last account
 * loaded wins and one workspace's backfill can write over another's. The worker
 * path is per-account and cannot cross that line.
 *
 * @param t - translator; runs outside React, so a thrown message is already in
 *   the reader's language
 */
export const requestBackfill = async (
  accountId: string,
  range: BackfillChunk,
  t: TFn,
): Promise<BackfillRequest> => {
  const chunks = monthlyChunks(range.start, range.end);
  if (chunks.length === 0) throw new Error(t('backfill.errBadRange'));

  const account = await getPlatformAccount(accountId);
  if (!account) throw new Error(t('settings.syncAccountNotFound'));
  // sync_jobs.workspace_id is NOT NULL, while PlatformAccount.workspaceId is
  // optional (the shape is also built for demo accounts, which have no row).
  // Without this guard an account missing it produced a Postgres 23502 naming
  // the column, not the account — so the check states the real precondition.
  if (!account.workspaceId) {
    throw new Error(`Account ${accountId} has no workspace; cannot queue a backfill`);
  }

  const supabase = getSupabaseClient();
  const rowFor = (c: BackfillChunk) => ({
    workspace_id: account.workspaceId as string,
    account_id: accountId,
    // The stored vocabulary, not the UI's — see triggerSync. Same slip, same
    // constraint (migration 080), same 400: a Google Ads or Google Analytics
    // backfill could never be queued.
    platform: toDbPlatform(account.platform),
    status: 'queued',
    start_date: c.start,
    end_date: c.end,
    source: 'backfill',
  });

  /** This account's backfill windows that are already waiting, as "start|end". */
  const waitingWindows = async (): Promise<Set<string>> => {
    const { data, error } = await supabase
      .from('sync_jobs')
      .select('start_date, end_date')
      .eq('account_id', accountId)
      .in('status', [...ACTIVE_JOB_STATUSES])
      .not('start_date', 'is', null);
    // Read as "nothing is waiting", a failed lookup re-sends months that are —
    // and the insert then fails whole on the first one.
    if (error) {
      logError('Backfill', 'Failed to read waiting backfill jobs', error);
      throw new Error(error.message || t('backfill.errQueueFailed'));
    }
    return new Set(((data ?? []) as Array<{ start_date: string; end_date: string }>).map((r) => `${r.start_date}|${r.end_date}`));
  };

  // Migration 103 allows one waiting job per account per window, and a
  // multi-row insert fails WHOLE on one refused row. So what is already waiting
  // (a second "Fetch history" over the same range, or the connect-time backfill
  // racing the panel) is left out first, and a request that lands between the
  // read and the insert costs one re-read and retry rather than the whole
  // batch. Once: refused again after the re-read, it throws and nothing from
  // THIS request is queued — a second click starts from a fresh read.
  let missing: BackfillChunk[] = [];
  for (let attempt = 1; ; attempt++) {
    const waiting = await waitingWindows();
    missing = chunks.filter((c) => !waiting.has(`${c.start}|${c.end}`));
    if (missing.length === 0) break;
    const { error } = await supabase.from('sync_jobs').insert(missing.map(rowFor));
    if (!error) break;
    if (isAlreadyQueued(error) && attempt === 1) continue;
    logError('Backfill', 'Failed to enqueue backfill', error);
    throw new Error(error.message || t('backfill.errQueueFailed'));
  }

  return {
    queued: missing.length,
    alreadyQueued: chunks.length - missing.length,
    // Reported oldest..newest regardless of the order they run in, because that
    // is the span a reader is asking about.
    first: chunks[chunks.length - 1].start,
    last: chunks[0].end,
  };
};
